import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { TextField } from '@design-system/TextField';
import { Spacing } from '@components/Spacing';
import { useLoading } from '@hooks/useLoading';
import { ErrorMessage } from '@components/ErrorMessage';
import { useErrorHandling } from '@hooks/useErrorHandling';
import { showSuccessMsg } from '@utils/notification';
import { 
  fetchGenerationPresets, 
  createGenerationPreset, 
  updateGenerationPreset, 
  deleteGenerationPreset,
  fetchPresetCategories,
  type GenerationPreset,
  type CreateGenerationPresetRequest,
  type UpdateGenerationPresetRequest,
  type PresetCategory
} from '@remotes/generationPresets';

const PresetsAdminDashboard: React.FC = () => {
  const [presets, setPresets] = useState<GenerationPreset[]>([]);
  const [availableCategories, setAvailableCategories] = useState<PresetCategory[]>([]);
  const [loading, startLoading, stopLoading] = useLoading();
  const [error, handleError, clearError] = useErrorHandling();
  const [editingPreset, setEditingPreset] = useState<GenerationPreset | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    prompt: '',
    description: '',
    visible: true,
    lora_url: '',
    foundation_model: 'FLUX' as 'CHATGPT' | 'FLUX' | 'NANO_BANANA',
    thumbnail: null as File | null,
    preview_before: null as File | null,
    preview_after: null as File | null,
    reference_images: [] as File[],
    categories: [] as PresetCategory[],
  });

  useEffect(() => {
    loadPresets();
    loadCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPresets = async () => {
    try {
      startLoading();
      clearError();
      const data = await fetchGenerationPresets();
      setPresets(data);
    } catch (err) {
      handleError(err);
    } finally {
      stopLoading();
    }
  };

  const loadCategories = async () => {
    try {
      const categories = await fetchPresetCategories();
      setAvailableCategories(categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
      // Set default categories if API fails
      setAvailableCategories(['ART_DESIGN_PHOTOGRAPHY', 'FUN_MEMES', 'FASHION_OUTFITS_BEAUTY', 'INTERIOR_DESIGN_PRODUCT']);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      prompt: '',
      description: '',
      visible: true,
      lora_url: '',
      foundation_model: 'FLUX',
      thumbnail: null,
      preview_before: null,
      preview_after: null,
      reference_images: [],
      categories: [],
    });
    setEditingPreset(null);
    setShowCreateForm(false);
    clearError();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearError();
  };

  const handleFileChange = (field: string, file: File | null) => {
    setFormData(prev => ({ ...prev, [field]: file }));
    clearError();
  };

  const handleMultipleFilesChange = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setFormData(prev => ({ ...prev, reference_images: [...prev.reference_images, ...fileArray] }));
      clearError();
    }
  };

  const removeReferenceImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      reference_images: prev.reference_images.filter((_, i) => i !== index)
    }));
  };

  const handleCategoryToggle = (category: PresetCategory) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
    clearError();
  };

  const formatCategoryName = (category: PresetCategory): string => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const handleSubmit = async () => {
    try {
      startLoading();
      clearError();

      if (!formData.title.trim() || !formData.prompt.trim()) {
        throw new Error('Title and prompt are required');
      }

      if (editingPreset) {
        // Update existing preset
        const updateData: UpdateGenerationPresetRequest = {
          title: formData.title,
          prompt: formData.prompt,
          description: formData.description,
          foundation_model: formData.foundation_model,
          visible: formData.visible,
          lora_url: formData.lora_url || null,
          preview_before: formData.preview_before,
          preview_after: formData.preview_after,
          categories: formData.categories,
        };
        
        // Add thumbnail if provided
        if (formData.thumbnail) {
          updateData.thumbnail = formData.thumbnail;
        }

        // Add reference images if provided
        if (formData.reference_images.length > 0) {
          updateData.uploaded_images = formData.reference_images;
        }

        const updatedPreset = await updateGenerationPreset(editingPreset.id, updateData);
        setPresets(prev => prev.map(p => p.id === updatedPreset.id ? updatedPreset : p));
        showSuccessMsg('Preset updated successfully!');
      } else {
        // Create new preset
        const createData: CreateGenerationPresetRequest = {
          title: formData.title,
          prompt: formData.prompt,
          description: formData.description,
          foundation_model: formData.foundation_model,
          visible: formData.visible,
          lora_url: formData.lora_url || null,
          uploaded_images: formData.reference_images,
          preview_before: formData.preview_before,
          preview_after: formData.preview_after,
          categories: formData.categories,
        };
        
        // Add thumbnail if provided
        if (formData.thumbnail) {
          createData.thumbnail = formData.thumbnail;
        }

        const newPreset = await createGenerationPreset(createData);
        setPresets(prev => [...prev, newPreset]);
        showSuccessMsg('Preset created successfully!');
      }

      resetForm();
      loadPresets(); // Refresh the list
    } catch (err) {
      handleError(err);
    } finally {
      stopLoading();
    }
  };

  const handleEdit = (preset: GenerationPreset) => {
    clearError();
    setFormData({
      title: preset.title,
      prompt: preset.prompt,
      description: preset.description || '',
      visible: preset.visible,
      lora_url: preset.lora_url || '',
      foundation_model: preset.foundation_model as 'CHATGPT' | 'FLUX' | 'NANO_BANANA',
      thumbnail: null,
      preview_before: null,
      preview_after: null,
      reference_images: [],
      categories: preset.categories || [],
    });
    setEditingPreset(preset);
    setShowCreateForm(true);
  };

  const handleDelete = async (preset: GenerationPreset) => {
    if (!window.confirm(`Are you sure you want to delete "${preset.title}"?`)) {
      return;
    }

    try {
      startLoading();
      await deleteGenerationPreset(preset.id);
      setPresets(prev => prev.filter(p => p.id !== preset.id));
      showSuccessMsg('Preset deleted successfully!');
    } catch (err) {
      handleError(err);
    } finally {
      stopLoading();
    }
  };

  const handleToggleVisible = async (preset: GenerationPreset) => {
    try {
      const updateData: UpdateGenerationPresetRequest = {
        visible: !preset.visible,
      };
      
      const updatedPreset = await updateGenerationPreset(preset.id, updateData);
      setPresets(prev => prev.map(p => p.id === updatedPreset.id ? updatedPreset : p));
      showSuccessMsg(`Preset ${updatedPreset.visible ? 'shown' : 'hidden'} successfully!`);
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <div className={containerStyle}>
      <div className={wrapperStyle}>
        <h1 className={titleStyle}>Generation Presets Dashboard</h1>
        <p className={subtitleStyle}>
          Manage generation presets for the platform
        </p>

        <Spacing size={40} />

        <ErrorMessage text={error} />
        
        <div className={headerActionsStyle}>
          <button 
            onClick={() => {
              if (showCreateForm) {
                resetForm();
              } else {
                resetForm();
                setShowCreateForm(true);
              }
            }}
            disabled={loading}
            className={showCreateForm ? secondaryButtonStyle : primaryButtonStyle}
          >
            {showCreateForm ? 'Cancel' : 'Create New Preset'}
          </button>
          
          <button 
            onClick={loadPresets}
            disabled={loading}
            className={secondaryButtonStyle}
          >
            Refresh
          </button>
        </div>

        <Spacing size={24} />

        {showCreateForm && (
          <div className={formSectionStyle}>
            <h3 className={formTitleStyle}>
              {editingPreset ? 'Edit Preset' : 'Create New Preset'}
            </h3>
            
            <div className={formStyle}>
              {/* First Row - Basic Info */}
              <div className={formRowStyle}>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Title *</label>
                    <TextField
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Enter preset title"
                    />
                  </div>
                </div>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Foundation Model *</label>
                    <div className={radioGroupStyle}>
                      {(['CHATGPT', 'FLUX', 'NANO_BANANA'] as const).map((model) => {
                        const displayNames = {
                          'CHATGPT': 'ChatGPT',
                          'FLUX': 'Flux',
                          'NANO_BANANA': 'Nano Banana'
                        };
                        return (
                          <label key={model} className={radioLabelStyle}>
                            <input
                              type="radio"
                              name="foundation_model"
                              value={model}
                              checked={formData.foundation_model === model}
                              onChange={(e) => handleInputChange('foundation_model', e.target.value)}
                              className={radioInputStyle}
                            />
                            <span className={radioTextStyle}>{displayNames[model]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Row - Prompt and Description */}
              <div className={formRowStyle}>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Prompt *</label>
                    <textarea
                      className={compactTextareaStyle}
                      value={formData.prompt}
                      onChange={(e) => handleInputChange('prompt', e.target.value)}
                      placeholder="Enter generation prompt"
                      rows={3}
                    />
                  </div>
                </div>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Description</label>
                    <textarea
                      className={compactTextareaStyle}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Enter preset description"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Categories Row */}
              <div className={fieldGroupStyle}>
                <label className={labelStyle}>Categories</label>
                <div className={categoriesContainerStyle}>
                  {availableCategories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleCategoryToggle(category)}
                      className={formData.categories.includes(category) ? categoryButtonActiveStyle : categoryButtonStyle}
                    >
                      {formatCategoryName(category)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Third Row - Visibility */}
              <div className={formRowStyle}>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Visibility</label>
                    <div className={radioGroupStyle}>
                      <label className={radioLabelStyle}>
                        <input
                          type="radio"
                          name="visible"
                          value="true"
                          checked={formData.visible === true}
                          onChange={(e) => handleInputChange('visible', true)}
                          className={radioInputStyle}
                        />
                        <span className={radioTextStyle}>Visible</span>
                      </label>
                      <label className={radioLabelStyle}>
                        <input
                          type="radio"
                          name="visible"
                          value="false"
                          checked={formData.visible === false}
                          onChange={(e) => handleInputChange('visible', false)}
                          className={radioInputStyle}
                        />
                        <span className={radioTextStyle}>Hidden</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className={formColumnStyle}>
                  {/* Empty column for layout balance */}
                </div>
              </div>

              {/* Fourth Row - Images Section */}
              <div className={formRowStyle}>
                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Thumbnail</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange('thumbnail', e.target.files?.[0] || null)}
                      className={compactFileInputStyle}
                    />
                    {editingPreset?.thumbnail && (
                      <div className={compactImageContainerStyle}>
                        <img 
                          src={editingPreset.thumbnail} 
                          alt="Current thumbnail" 
                          className={compactImagePreviewStyle}
                          onClick={() => editingPreset.thumbnail && window.open(editingPreset.thumbnail, '_blank')}
                        />
                      </div>
                    )}
                  </div>

                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>Before Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange('preview_before', e.target.files?.[0] || null)}
                      className={compactFileInputStyle}
                    />
                    {editingPreset?.preview_before && (
                      <div className={compactImageContainerStyle}>
                        <img 
                          src={editingPreset.preview_before} 
                          alt="Current before" 
                          className={compactImagePreviewStyle}
                          onClick={() => editingPreset.preview_before && window.open(editingPreset.preview_before, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className={formColumnStyle}>
                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>After Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange('preview_after', e.target.files?.[0] || null)}
                      className={compactFileInputStyle}
                    />
                    {editingPreset?.preview_after && (
                      <div className={compactImageContainerStyle}>
                        <img 
                          src={editingPreset.preview_after} 
                          alt="Current after" 
                          className={compactImagePreviewStyle}
                          onClick={() => editingPreset.preview_after && window.open(editingPreset.preview_after, '_blank')}
                        />
                      </div>
                    )}
                  </div>

                  <div className={fieldGroupStyle}>
                    <label className={labelStyle}>LORA URL</label>
                    <TextField
                      value={formData.lora_url}
                      onChange={(e) => handleInputChange('lora_url', e.target.value)}
                      placeholder="Enter LORA file URL (optional)"
                    />
                  </div>
                </div>
              </div>

              {/* Fourth Row - Reference Images */}
              <div className={fieldGroupStyle}>
                <label className={labelStyle}>Reference Images</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleMultipleFilesChange(e.target.files)}
                  className={compactFileInputStyle}
                />
                <div className={imagePreviewHelpStyle}>
                  Upload reference images to help guide the generation process.
                </div>
                
                <div className={imagesDisplayRowStyle}>
                  {/* Show uploaded reference images */}
                  {formData.reference_images.length > 0 && (
                    <div className={compactImagesContainerStyle}>
                      <span className={compactImageLabelStyle}>New uploads ({formData.reference_images.length}):</span>
                      <div className={compactImageGridStyle}>
                        {formData.reference_images.map((file, index) => (
                          <div key={index} className={compactImageItemStyle}>
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt={`Reference ${index + 1}`} 
                              className={compactImageThumbStyle}
                            />
                            <button
                              type="button"
                              onClick={() => removeReferenceImage(index)}
                              className={removeImageButtonStyle}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show existing reference images when editing */}
                  {editingPreset && editingPreset.images.length > 0 && (
                    <div className={compactImagesContainerStyle}>
                      <span className={compactImageLabelStyle}>Current ({editingPreset.images.length}):</span>
                      <div className={compactImageGridStyle}>
                        {editingPreset.images.slice(0, 8).map((image, index) => (
                          <div key={image.id} className={compactImageItemStyle}>
                            <img 
                              src={image.small} 
                              alt={`Existing ${index + 1}`} 
                              className={compactImageThumbStyle}
                              onClick={() => window.open(image.full, '_blank')}
                            />
                          </div>
                        ))}
                        {editingPreset.images.length > 8 && (
                          <div className={moreImagesIndicatorStyle}>
                            +{editingPreset.images.length - 8}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Fifth Row - Settings and Actions */}
              <div className={formRowStyle}>
                <div className={formColumnStyle}>
                  <div className={checkboxGroupStyle}>
                    <label className={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={formData.visible}
                        onChange={(e) => handleInputChange('visible', e.target.checked)}
                      />
                      Visible to users
                    </label>
                  </div>
                </div>
                <div className={formColumnStyle}>
                  <div className={compactButtonGroupStyle}>
                    <button 
                      onClick={handleSubmit} 
                      disabled={loading}
                      className={primaryButtonStyle}
                    >
                      {editingPreset ? 'Update Preset' : 'Create Preset'}
                    </button>
                    <button 
                      onClick={() => {
                        resetForm();
                        clearError();
                      }} 
                      disabled={loading}
                      className={secondaryButtonStyle}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Spacing size={32} />

        <div className={presetsListStyle}>
          <h3 className={sectionTitleStyle}>Existing Presets ({presets.length})</h3>
          
          {loading && !showCreateForm && <div className={loadingStyle}>Loading presets...</div>}
          
          {presets.length === 0 && !loading && (
            <div className={emptyStateStyle}>No presets found.</div>
          )}

          {presets.map((preset) => (
            <div key={preset.id} className={presetCardStyle}>
              <div className={presetHeaderStyle}>
                <div>
                  <h4 className={presetTitleStyle}>{preset.title}</h4>
                  <p className={presetModelStyle}>{preset.foundation_model}</p>
                </div>
                <div className={presetStatusStyle}>
                  <span className={preset.visible ? visibleBadgeStyle : hiddenBadgeStyle}>
                    {preset.visible ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
              
              <p className={presetPromptStyle}>{preset.prompt}</p>
              
              {preset.description && (
                <p className={presetDescriptionStyle}>{preset.description}</p>
              )}

              {/* Categories Display */}
              {preset.categories && preset.categories.length > 0 && (
                <div className={presetCategoriesDisplayStyle}>
                  <span className={presetCategoriesLabelStyle}>Categories:</span>
                  <div className={presetCategoriesListStyle}>
                    {preset.categories.map((category) => (
                      <span key={category} className={presetCategoryTagStyle}>
                        {formatCategoryName(category)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(preset.preview_before || preset.preview_after) && (
                <div className={presetImagesContainerStyle}>
                  {preset.preview_before && (
                    <div className={presetImageWrapperStyle}>
                      <span className={presetImageLabelStyle}>Before:</span>
                      <img 
                        src={preset.preview_before} 
                        alt="Before" 
                        className={presetImageStyle}
                        onClick={() => preset.preview_before && window.open(preset.preview_before, '_blank')}
                      />
                    </div>
                  )}
                  {preset.preview_after && (
                    <div className={presetImageWrapperStyle}>
                      <span className={presetImageLabelStyle}>After:</span>
                      <img 
                        src={preset.preview_after} 
                        alt="After" 
                        className={presetImageStyle}
                        onClick={() => preset.preview_after && window.open(preset.preview_after, '_blank')}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Reference Images Display */}
              {preset.images && preset.images.length > 0 && (
                <div className={presetImagesContainerStyle}>
                  <div className={presetImageWrapperStyle}>
                    <span className={presetImageLabelStyle}>Reference Images ({preset.images.length}):</span>
                    <div className={referenceImagesGridStyle}>
                      {preset.images.slice(0, 4).map((image, index) => (
                        <img 
                          key={image.id}
                          src={image.small} 
                          alt={`Reference ${index + 1}`} 
                          className={referenceImageThumbStyle}
                          onClick={() => window.open(image.full, '_blank')}
                          title={`Reference image ${index + 1} - Click to view full size`}
                        />
                      ))}
                      {preset.images.length > 4 && (
                        <div className={moreImagesIndicatorStyle}>
                          +{preset.images.length - 4} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className={presetInfoStyle}>
                <span>Created: {new Date(preset.created_at).toLocaleDateString()}</span>
                {preset.lora_url && <span>Has LORA file</span>}
                <span>Images: {preset.images?.length || 0}</span>
                {preset.preview_before && <span>Has before photo</span>}
                {preset.preview_after && <span>Has after photo</span>}
              </div>

              <div className={presetActionsStyle}>
                <button 
                  onClick={() => handleEdit(preset)}
                  className={actionButtonStyle}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleToggleVisible(preset)}
                  className={actionButtonStyle}
                >
                  {preset.visible ? 'Hide' : 'Show'}
                </button>
                <button 
                  onClick={() => handleDelete(preset)}
                  className={deleteButtonStyle}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = css`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  min-height: 100vh;
  background: #1a1a1a;
`;

const wrapperStyle = css`
  max-width: 1000px;
  width: 100%;
`;

const titleStyle = css`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin: 0;
`;

const subtitleStyle = css`
  font-size: 16px;
  font-weight: 400;
  color: #aaaaaa;
  text-align: center;
  margin: 8px 0 0 0;
`;

const headerActionsStyle = css`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const formSectionStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
`;

const formTitleStyle = css`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 24px 0;
`;

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const formRowStyle = css`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const formColumnStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const fieldGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const labelStyle = css`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const compactTextareaStyle = css`
  box-sizing: border-box;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  outline: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: #aaaaaa;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: #007bff;
  }
`;

const compactFileInputStyle = css`
  width: 100%;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;

  &::file-selector-button {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    color: #ffffff;
    margin-right: 8px;
    cursor: pointer;
    font-size: 11px;
    transition: background 0.2s ease;
  }

  &::file-selector-button:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const compactImageContainerStyle = css`
  margin-top: 8px;
  display: flex;
  align-items: center;
`;

const compactImagePreviewStyle = css`
  width: 60px;
  height: 45px;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    transform: scale(1.05);
  }
`;

const imagesDisplayRowStyle = css`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const compactImagesContainerStyle = css`
  flex: 1;
  min-width: 200px;
`;

const compactImageLabelStyle = css`
  display: block;
  font-size: 11px;
  color: #aaaaaa;
  margin-bottom: 8px;
`;

const compactImageGridStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const compactImageItemStyle = css`
  position: relative;
  display: flex;
  align-items: center;
`;

const compactImageThumbStyle = css`
  width: 40px;
  height: 40px;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    transform: scale(1.1);
  }
`;

const radioGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const radioLabelStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const radioInputStyle = css`
  margin: 0;
  accent-color: #007bff;
`;

const radioTextStyle = css`
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  text-transform: capitalize;
`;

const presetImagesContainerStyle = css`
  display: flex;
  gap: 16px;
  margin: 16px 0;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const presetImageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const presetImageLabelStyle = css`
  font-size: 12px;
  color: #aaaaaa;
  font-weight: 500;
`;

const presetImageStyle = css`
  width: 120px;
  height: 90px;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: block;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
`;

const imagePreviewHelpStyle = css`
  font-size: 12px;
  color: #aaaaaa;
  margin-top: 8px;
  line-height: 1.4;
`;

const removeImageButtonStyle = css`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: #ff3742;
  }
`;

const referenceImagesGridStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
  align-items: center;
`;

const referenceImageThumbStyle = css`
  width: 40px;
  height: 40px;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: block;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`;

const moreImagesIndicatorStyle = css`
  font-size: 11px;
  color: #888888;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const checkboxGroupStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const checkboxLabelStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ffffff;
  cursor: pointer;

  input[type="checkbox"] {
    accent-color: #007bff;
  }
`;

const compactButtonGroupStyle = css`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const primaryButtonStyle = css`
  background-color: #007bff;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background-color: #0056b3;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const secondaryButtonStyle = css`
  background-color: transparent;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const presetsListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const sectionTitleStyle = css`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const loadingStyle = css`
  text-align: center;
  color: #aaaaaa;
  padding: 40px;
`;

const emptyStateStyle = css`
  text-align: center;
  color: #aaaaaa;
  padding: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
`;

const presetCardStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const presetHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const presetTitleStyle = css`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
`;

const presetModelStyle = css`
  font-size: 12px;
  color: #888888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
`;

const presetStatusStyle = css`
  display: flex;
  align-items: center;
`;

const visibleBadgeStyle = css`
  padding: 4px 8px;
  background: #4caf50;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const hiddenBadgeStyle = css`
  padding: 4px 8px;
  background: #757575;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const presetPromptStyle = css`
  color: #dddddd;
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 8px 0;
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
`;

const presetDescriptionStyle = css`
  color: #aaaaaa;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 12px 0;
`;

const presetInfoStyle = css`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #888888;
  margin-bottom: 16px;

  span {
    display: flex;
    align-items: center;
  }
`;

const presetActionsStyle = css`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const actionButtonStyle = css`
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const deleteButtonStyle = css`
  padding: 6px 12px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 6px;
  color: #ff6b6b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.2);
    border-color: rgba(255, 107, 107, 0.5);
  }
`;


const categoriesContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const categoryButtonStyle = css`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const categoryButtonActiveStyle = css`
  padding: 8px 16px;
  background: #007bff;
  border: 1px solid #007bff;
  border-radius: 20px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #0056b3;
    border-color: #0056b3;
  }
`;

const presetCategoriesDisplayStyle = css`
  margin: 12px 0;
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const presetCategoriesLabelStyle = css`
  font-size: 12px;
  color: #aaaaaa;
  font-weight: 500;
  display: block;
  margin-bottom: 8px;
`;

const presetCategoriesListStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const presetCategoryTagStyle = css`
  padding: 4px 8px;
  background: rgba(0, 123, 255, 0.2);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 12px;
  color: #87ceeb;
  font-size: 11px;
  font-weight: 500;
`;

export default PresetsAdminDashboard;